import { Link } from "react-router-dom";
import { Clock, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/SiteContext";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Our Services", to: "/services" },
  { label: "Industries We Serve", to: "/#industries" },
  { label: "Contact Us", to: "/contact" },
  { label: "Get a Quote", to: "/contact?quote=1" },
];

export const Footer = () => {
  const { content } = useSite();
  const services = content?.services || [];
  const social = content?.social || {};

  const socials = [
    { icon: Facebook, href: social.facebook, label: "Facebook" },
    { icon: Instagram, href: social.instagram, label: "Instagram" },
    { icon: Linkedin, href: social.linkedin, label: "LinkedIn" },
    { icon: Twitter, href: social.twitter, label: "Twitter" },
  ];

  return (
    <footer className="border-t border-gold/25 bg-navy-950 text-slate-300" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
        <div>
          <Logo />
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Integrated business solutions under one trusted roof — consulting, healthcare support, property,
            events, facilities, housekeeping, security, administration, and manpower.
          </p>
          <p className="mt-4 font-serif text-base italic text-gold" data-testid="footer-tagline">
            "Together for a Better Tomorrow."
          </p>
          <div className="mt-6 flex gap-3">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href || "#"}
                aria-label={`SSS GROUPS on ${label}`}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:text-gold"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Quick links">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">Quick Links</h4>
          <ul className="mt-5 space-y-3 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="transition-colors hover:text-gold">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Services">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">Our Services</h4>
          <ul className="mt-5 space-y-3 text-sm">
            {services.map((s) => (
              <li key={s.slug}>
                <Link to={`/services#${s.slug}`} className="transition-colors hover:text-gold">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contact</h4>
          <ul className="mt-5 space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a href={`tel:${(content?.phone || "").replace(/\s/g, "")}`} className="hover:text-gold">
                {content?.phone || "+91 90000 00000"}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a href={`mailto:${content?.email || "info@sssgroups.com"}`} className="hover:text-gold">
                {content?.email || "info@sssgroups.com"}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>{content?.address || "Office address — to be updated"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span>{content?.hours || "Monday – Saturday: 9:00 AM – 6:00 PM"}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 text-xs text-slate-500 sm:flex-row lg:px-10">
          <p data-testid="footer-copyright">© 2026 SIREE SOLUTIONS GROUPS (SSS GROUPS). All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="transition-colors hover:text-gold" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="transition-colors hover:text-gold" data-testid="footer-terms-link">
              Terms &amp; Conditions
            </Link>
            <Link to="/admin" className="transition-colors hover:text-gold" data-testid="footer-admin-link">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
